import React, {useMemo, useState} from 'react'
import {nanoid} from 'nanoid'
import {BlockProps} from '../atoms/blockList'
import {ListInput, FieldProps, ListValue} from '../atoms/listInput'
import {
  Configs,
  Reference,
  ReferenceButton,
  RefSelectDrawer,
  isFunctionalUpdate,
} from '@dudagroup/editor'
import {FaqListBlockValue} from './types'
import {Drawer} from 'rsuite'

// Your storage types for reference:
// export interface ListWrapper<T = unknown> { id: string; expanded: boolean; content: T }
// export interface Reference<T = any> { recordId: string; contentType: string; record?: T }
// export interface FaqListBlockValue { faqList: ListWrapper<Reference>[] }

// default Reference used when adding a new item
const newReference = (): Reference => ({recordId: '', contentType: ''})

export function FaqListBlock({value, onChange, configs, disabled}: BlockProps<FaqListBlockValue>) {
  // Adapt storage -> UI
  const listValues: Array<ListValue<Reference>> = useMemo(
    () => value.faqList.map((w) => ({id: w.id, value: w.content})),
    [value.faqList]
  )

  function handleItemsChange(items: React.SetStateAction<Array<ListValue<Reference>>>) {
    const nextLV = isFunctionalUpdate(items) ? items(listValues) : items

    // Preserve `expanded` by id; default to false for new ids
    const expandedById = new Map(value.faqList.map((w) => [w.id, w.expanded]))

    const nextFaqList = nextLV.map((lv) => ({
      id: lv.id ?? nanoid(),
      expanded: expandedById.get(lv.id) ?? false,
      content: lv.value,
    }))

    onChange(nextFaqList, ['faqList'])
  }

  return (
    <ListInput<Reference>
      value={listValues}
      onChange={handleItemsChange}
      defaultValue={newReference} // can be a function; each add gets a fresh object
      disabled={disabled}>
      {(props) => <FaqListElement {...props} configs={configs} />}
    </ListInput>
  )
}

function FaqListElement({value, configs, onChange}: FieldProps<Reference> & {configs: Configs}) {
  const [isChooseModalOpen, setChooseModalOpen] = useState(false)

  return (
    <>
      <div style={{flexGrow: 1, margin: 2}}>
        <ReferenceButton
          configs={configs}
          reference={value.recordId ? value : null}
          onClick={() => setChooseModalOpen(true)}
          onClose={() => onChange((v) => ({...v, recordId: '', contentType: ''}))}
        />
        <Drawer
          placement="bottom"
          open={isChooseModalOpen}
          size="full"
          onClose={() => setChooseModalOpen(false)}>
          <RefSelectDrawer
            configs={configs}
            refConfig={{faq: {scope: 'local'}}}
            onClose={() => setChooseModalOpen(false)}
            onSelectRef={(ref) => {
              setChooseModalOpen(false)
              onChange((v) => ({...v, ...ref}))
            }}
          />
        </Drawer>
      </div>
    </>
  )
}
